import React, { useEffect } from 'react';
import * as R from 'ramda';
import { v4 as uuidv4 } from 'uuid';
import { createSelector } from 'reselect';
import { useSelector } from 'react-redux';

import { getConnectionHealth } from 'reducers/connectivity/connection';
import { PosPlugin } from 'plugins/plugin';
import { openPluginModalPage } from 'actions/modalPage';
import { combineLifecycleHooks } from 'plugins/pluginUtils';
import { addProduct } from 'actions/ShoppingCart';
import { addError, addWarning } from 'actions/Error';
import { getProductInOrderByIndex } from 'reducers/ShoppingCart';

import { Conf } from './types';
import { pluginID } from './constants';
import { customPaymentIntegration } from './payment/CustomPaymentIntegration';
import { modalComponents, modals } from './modals';
import * as API from './API/givexAPI';
import {
  ComponentConfiguration,
  getGivexConfiguration,
  getGivexURL,
  getisGivexAdmin,
  getIsTransferRequired,
} from './configuration/Configuration';
import {
  addGivexRefillPaymentsFromCart,
  convertOriginalGiftcardPaymentsBackToGivex,
  disableGiftcardTenderAndRemoveGivexPayment,
  postProcessPayments,
  makeGivexRefillLastPayment,
  applyGivexCardBalance,
  restrictReturningGivexIncrementProduct,
} from './payment/overrides';
import KeyPad from './payment/Keypad';
import OriginalPayment from './payment/OriginalPayment';
import GiftCard from './payment/GiftCard';

const GivexHeartland: PosPlugin<Conf> = {
  id: pluginID,
  name: 'Givex heartland',

  /* Configuraton */
  ComponentConfigurationByLevel: {
    Company: ComponentConfiguration,
  },
  combineConfiguration: c =>
    // Typescript definitions for mergeDeepLeft do not know that null / undefined is allowed here
    R.mergeDeepLeft(c as Conf)({
      givexUrl: 'wss://localhost.erply.com:5658',
      isPinRequired: false,
      defaultCardNumber: '',
      defaultPin: '',
      isTransferEnabled: false,
      adminId: '-1',
      isAllowedOnSale: true,
      isAllowedOnReturnWithoutReceipt: true,
      isAllowedOnReturnWithReceipt: true,
    } as Conf),
  getStatus: state => {
    const { giftcardProductID } = getGivexConfiguration(state);
    if (!giftcardProductID || giftcardProductID === '-1') {
      return {
        type: 'error',
        message: 'Missing product to use as Givex increment',
      };
    }
    return { type: 'valid', message: 'Ready' };
  },

  /* UI Entry points (custom buttons) */
  getCustomButtons: createSelector(
    getIsTransferRequired,
    getisGivexAdmin,
    getGivexConfiguration,
    getConnectionHealth,
    (isTransferEnabled, isAdmin, conf, isOnline) => {
      return {
        functions: [
          {
            id: uuidv4(),
            name: 'Givex balance',
            actionType: 'action',
            disabled: !isOnline,
            action: openPluginModalPage(modals.giftCardBalance)({}),
          },
          {
            id: uuidv4(),
            name: 'Givex adjust',
            actionType: 'action',
            disabled: !isOnline,
            hide: !isAdmin,
            action: openPluginModalPage(modals.giftCardAdjust)({}),
          },
          {
            id: uuidv4(),
            name: 'Givex transfer',
            actionType: 'action',
            disabled: !isOnline,
            hide: !isTransferEnabled,
            action: openPluginModalPage(modals.giftCardTransfer)({}),
          },
          {
            id: uuidv4(),
            name: 'Givex increment',
            actionType: 'action',
            disabled: !isOnline,
            action:
              !conf.giftcardProductID || conf.giftcardProductID === '-1'
                ? addError('Givex increment product is not configured')
                : addProduct({
                    productID: conf.giftcardProductID,
                    allowNegativePrice: false,
                  }),
          },
        ],
      };
    },
  ),
  onAddProduct: {
    before: product => async (dispatch, getState) => {
      const givexConf = getGivexConfiguration(getState());
      if (
        givexConf.giftcardProductID &&
        product.productID === Number(givexConf.giftcardProductID) &&
        product.allowNegativePrice
      ) {
        dispatch(
          // @ts-ignore
          addProduct({
            ...product,
            allowNegativePrice: false,
          }),
        );
        throw new Error(
          'Givex increment was added via product search. Replacing props via givex plugin',
        );
      }
    },
    on: (params, ap) => async (dispatch, getState) => {
      const config = getGivexConfiguration(getState());
      if (ap.product.productID === config.giftcardProductID) {
        return {
          ...ap,
          product: {
            ...ap.product,
            uuid: uuidv4(),
          },
        };
      }
      return ap;
    },
  },
  UIKeyPad: KeyPad,
  UIGiftCard: GiftCard,
  UIOriginalPayment: OriginalPayment,
  customPaymentIntegration,
  components: modalComponents,

  onOpenPaymentModal: combineLifecycleHooks(
    { on: addGivexRefillPaymentsFromCart },
    { on: disableGiftcardTenderAndRemoveGivexPayment },
  ),
  onSetPayment: {
    on: applyGivexCardBalance,
  },
  onSetCurrentSalesDocPayments: {
    on: convertOriginalGiftcardPaymentsBackToGivex,
  },
  onSaveSalesDocument: {
    on: postProcessPayments,
  },
  onProcessPayments: { on: makeGivexRefillLastPayment },

  ComponentHeader: ({ children }) => {
    const url = useSelector(getGivexURL);
    useEffect(() => {
      API.updateGivexURL(url);
    }, [url]);

    return children;
  },

  onUpdateOrderAmount: {
    before: restrictReturningGivexIncrementProduct,
    on: (p, ap) => async (dispatch, getState) => {
      const state = getState();
      const conf = getGivexConfiguration(state);
      const order = getProductInOrderByIndex(p.orderIndex)(state);
      if (
        order.productID === conf.giftcardProductID &&
        Math.abs(Number(ap.amount)) > 1
      ) {
        dispatch(
          addWarning('Only one Givex Increment product can be sold per row'),
        );
        return {
          ...ap,
          amount: order.amount,
        };
      }
      return ap;
    },
  },
};

export default GivexHeartland;
